/**
 * Extract a thumbnail from a video file at a specific timestamp (default 1.0s).
 * Returns { blob, dataUrl, durationSec, width, height } or throws on failure.
 */
export async function extractVideoThumbnail(file, seekSec = 1.0, maxWidth = 640, quality = 0.7) {
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;
    video.crossOrigin = 'anonymous';

    await new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('Video metadata load timeout')), 10000);
      video.onloadedmetadata = () => { clearTimeout(t); resolve(); };
      video.onerror = () => { clearTimeout(t); reject(new Error('Video failed to load')); };
    });

    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const target = Math.min(Math.max(0.1, seekSec), Math.max(0.1, duration - 0.1));
    video.currentTime = target;

    await new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('Video seek timeout')), 10000);
      video.onseeked = () => { clearTimeout(t); resolve(); };
    });

    const scale = Math.min(1, maxWidth / (video.videoWidth || maxWidth));
    const w = Math.round((video.videoWidth || maxWidth) * scale);
    const h = Math.round((video.videoHeight || maxWidth * 9 / 16) * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(video, 0, 0, w, h);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) throw new Error('Canvas toBlob returned null (CORS or format issue)');
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    return { blob, dataUrl, durationSec: duration, width: w, height: h };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Format seconds as m:ss */
export function formatSeconds(s) {
  if (!Number.isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}
